"use client";
import { initializeApp, getApp, getApps, FirebaseApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import { firebaseConfig } from "./firebase.config";
import {
  NextOrObserver,
  User,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { translatedVariants } from "../Utils/translationObject";
import {
  getDownloadURL,
  getStorage,
  uploadBytes,
  uploadString,
  ref,
  StorageReference,
} from "firebase/storage";
import { AppNotify } from "../Utils/AppNotify";
let id: string | null = "";
let app;
if (typeof window !== "undefined") {
  id = localStorage.getItem("TUTORCERT_LANGUAGE");
}
const langObj =
  translatedVariants[
    id === "" || !id ? "en" : (id as keyof typeof translatedVariants)
  ];

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
  isSupported().then((yes) => (yes ? getAnalytics(app) : null));
}

export const imageDb = getStorage(app);
export const auth = getAuth(getApp());
export const db = getFirestore(app as FirebaseApp);
const provider = new GoogleAuthProvider();

export const createUserDocumentFromAuth = async (
  userAuth: User,
  additionalInformation = {}
) => {
  if (!userAuth) return;

  const userDocRef = doc(db, "users", userAuth.uid);
  const userSnapshot = await getDoc(userDocRef);

  if (!userSnapshot.exists()) {
    const { email } = userAuth;
    const createdAt = new Date();

    try {
      await setDoc(userDocRef, {
        email,
        createdAt,
        type: "user",
        ...additionalInformation,
      });

      return {
        status: true,
      };
    } catch (error) {}
  }

  return userSnapshot;
};

export const createAuthUserWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  if (!email || !password) return;
  try {
    return await createUserWithEmailAndPassword(auth, email, password);
  } catch (error) {}
};

export const googleAuthentication = async () => {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    await createUserDocumentFromAuth(user, { provider: "google" });
    return {
      status: true,
    };
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const checkIfGoogleAuthEmail = async (email: string) => {
  try {
    const q = query(collection(db, "users"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.length > 0) {
      const docData = querySnapshot.docs[0].data();
      if (docData.provider === "google") {
        return {
          googleEmail: true,
        };
      } else {
        return {
          googleEmail: false,
        };
      }
    } else {
      return {
        googleEmail: false,
      };
    }
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const signInAuthUserWithEmailAndPassword = async (
  email: string,
  password: string,
  dontShowError?: boolean
) => {
  if (!email || !password) return;
  try {
    const resp = await checkIfGoogleAuthEmail(email);
    if (resp.googleEmail) {
      AppNotify(langObj.google_email, "error");
      return null;
    }
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    if (!dontShowError) {
      AppNotify(langObj.wrong_email_or_password, "error");
    }
  }
};

export const sendPasswordResetEmailFunc = async (email: string) => {
  if (!email) return;
  try {
    return await sendPasswordResetEmail(auth, email, {
      url: "https://tutorcert-324d6.web.app/login",
    });
  } catch (error) {
    AppNotify(langObj.error_sending_pass_reset, "error");
  }
};

export const signOutUser = async () => await signOut(auth);

export const onAuthStateChangedListener = (callback: NextOrObserver<User>) =>
  onAuthStateChanged(auth, callback);

export const checkAccountExists = async (matchVal: string) => {
  try {
    const q = query(collection(db, "users"), where("email", "==", matchVal));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.length > 0) {
      return {
        status: true,
        exists: true,
      };
    } else {
      return {
        status: true,
        exists: false,
      };
    }
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const getDocument = async (id: string, collectionName: string) => {
  try {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        exists: true,
        status: true,
        data: docSnap.data(),
      };
    } else {
      return {
        exists: false,
        status: true,
      };
    }
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const checkOldPassword = async (email: string, password: string) => {
  try {
    const user = await reauthenticateWithCredential(
      auth.currentUser as User,
      EmailAuthProvider.credential(email, password)
    );
    if (user.user) {
      return {
        status: true,
      };
    }
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const addDocInCollection = async (
  collectionName: string,
  data: any,
  dontShowMsg?: boolean,
  examsCompleted?: number,
  examsAttempted?: number,
  createdExamId?: string | null
) => {
  try {
    const batch = writeBatch(db);
    const docRef = doc(collection(db, collectionName));
    let updateDocRef: any = null;
    if (createdExamId) {
      updateDocRef = doc(db, collectionName, createdExamId);
    }

    if (updateDocRef) {
      batch.set(updateDocRef, data, { merge: true });
    } else {
      batch.set(docRef, data);
    }

    if (collectionName === "exams_completed") {
      const userId = data.user_id;
      batch.set(
        doc(db, "users", userId as string),
        {
          attempted_exams: examsAttempted ? examsAttempted + 1 : 1,
          completed_exams: examsCompleted ? examsCompleted + 1 : 1,
        },
        { merge: true }
      );
    }

    await batch.commit();

    if (!dontShowMsg) {
      AppNotify("Added Successfully!", "success");
    }

    return {
      status: true,
      docId: docRef && docRef.id ? docRef.id : "",
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
      docId: "",
    };
  }
};

export const updateCollection = async (
  collectionName: string,
  id: string,
  data: any,
  dontShowMessage?: boolean
) => {
  try {
    const batch = writeBatch(db);
    batch.set(
      doc(db, collectionName, id),
      {
        ...data,
      },
      {
        merge: true,
      }
    );

    await batch.commit();

    if (!dontShowMessage) {
      AppNotify("Updated Successfully!", "success");
    }
    return {
      status: true,
    };
  } catch (error) {
    if (!dontShowMessage) {
      AppNotify("An unknown error occured. Try again later", "error");
    }
    return {
      status: false,
    };
  }
};
export const getAllDocs = async (collectionName: string) => {
  try {
    const q = query(collection(db, collectionName));
    const querySnapshot = await getDocs(q);
    const data: any = [];
    querySnapshot.forEach((doc) => {
      data.push({ ...doc.data(), docId: doc.id });
    });
    return {
      status: true,
      data: data[0],
      fullData: data,
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
    };
  }
};

export const getFilterExams = async (filters: any, dateRange: any) => {
  try {
    let startDate =
      dateRange && dateRange.startDate ? Date.parse(dateRange.startDate) : null;
    let endDate =
      dateRange && dateRange.endDate ? Date.parse(dateRange.endDate) : null;

    const conditions: any = [];

    if (filters && filters.payment) {
      conditions.push(where("payment", "==", filters.payment));
    }

    if (filters && filters.complete) {
      conditions.push(where("status", "==", filters.complete));
    }

    if (startDate && endDate) {
      conditions.push(where("completed_at", ">", startDate));
      conditions.push(where("completed_at", "<", endDate));
    }

    const q = query(collection(db, "exams_completed"), ...conditions);
    const querySnapshot = await getDocs(q);
    const data: any = [];

    querySnapshot.forEach((doc) => {
      data.push({ ...doc.data(), docId: doc.id });
    });
    return {
      status: true,
      data: data[0],
      fullData: data,
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
    };
  }
};

export const getExamsUsingUsersIds = async (usersIds: any) => {
  try {
    if (usersIds && usersIds.length) {
      let exams: any = [];
      const resultUsers: any = [];

      for (let i = 0; i < usersIds.length; i += 10) {
        resultUsers.push(usersIds.slice(i, i + 10));
      }

      const examSnapsCollection: any = [];

      for await (const snap of resultUsers.map(
        async (chunk: any) =>
          await getDocs(
            query(
              collection(db, "exams_completed"),
              where("user_id", "in", chunk)
            )
          )
      )) {
        examSnapsCollection.push(snap);
      }

      const docsSnapshots = await Promise.all(examSnapsCollection);

      exams = docsSnapshots
        .flatMap((querySnapshot) => querySnapshot.docs)
        .map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));

      if (exams) {
        return exams;
      } else {
        return null;
      }
    }
  } catch (error) {}
};

export const uploadImage = async (imgFile: any, imgId?: any) => {
  try {
    const id = imgId || "id" + Math.random().toString(16).slice(2);
    const imgRef: any = ref(imageDb as any, `images/${id}`);
    await uploadBytes(imgRef, imgFile);
    let downloadUrl = "";
    if (!imgId) {
      downloadUrl = await getDownloadURL(imgRef);
    }
    return {
      status: true,
      id,
      downloadUrl,
    };
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const uploadWebcamImage = async (imgSrc: any, imgId?: any) => {
  try {
    const id = imgId || "id" + Math.random().toString(16).slice(2);
    const imageStorageRef: StorageReference = ref(imageDb, `images/${id}`);

    // perform the upload
    await uploadString(imageStorageRef, imgSrc, "data_url");

    let downloadUrl = "";

    if (!imgId) {
      downloadUrl = await getDownloadURL(imageStorageRef);
    }
    return {
      status: true,
      id,
      downloadUrl,
    };
  } catch (error) {
    return {
      status: false,
    };
  }
};

export const deleteDocument = async (collectionName: string, id: string) => {
  try {
    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);
    AppNotify("Deleted Successfully", "success");
    return {
      status: true,
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
    };
  }
};

export const queryData = async (
  collectionName: string,
  field: string,
  matchVal: any
) => {
  try {
    let q = query(collection(db, collectionName), where(field, "==", matchVal));
    if (collectionName === "exams_questions") {
      q = query(
        collection(db, collectionName),
        where(field, "==", matchVal),
        where("is_archived", "==", false)
      );
    }

    const querySnapshot = await getDocs(q);
    const data: any = [];
    querySnapshot.forEach((doc) => {
      data.push({ ...doc.data(), docId: doc.id });
    });
    return {
      status: true,
      data: data[0],
      fullData: data,
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
    };
  }
};

export const getActiveExam = async (matchVal: string) => {
  try {
    const q = query(
      collection(db, "exams"),
      where("language", "==", matchVal),
      where("is_archived", "==", false),
      where("is_active", "==", true)
    );
    const querySnapshot = await getDocs(q);
    const data: any = [];
    querySnapshot.forEach((doc) => {
      data.push({ ...doc.data(), docId: doc.id });
    });
    return {
      status: true,
      data: data[0],
      fullData: data,
    };
  } catch (error) {
    AppNotify("An unknown error occured. Try again later", "error");
    return {
      status: false,
    };
  }
};

export const getFilterUsers = async (fieldName: string, matchVal: string) => {
  try {
    const q = query(
      collection(db, "users"),
      where(`${fieldName}`, ">=", matchVal),
      where(`${fieldName}`, "<=", matchVal + "\uF8FF")
    );

    const querySnapshot = await getDocs(q);
    const data: any = [];

    querySnapshot.forEach((doc) => {
      data.push({ ...doc.data(), docId: doc.id });
    });

    return data;
  } catch (error) {
    return null;
  }
};

export const deleteDocumentQuery = async (
  param: string,
  value: string,
  tableName: string
) => {
  try {
    const batch = writeBatch(db);
    const docQuery = query(
      collection(db, tableName),
      where(param, "==", value)
    );
    const docsData = await getDocs(docQuery);
    docsData.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();
  } catch (error) {}
};

export const deleteTransaction = async (
  certId: string,
  transId: string,
  examId: string
) => {
  try {
    const batch = writeBatch(db);
    const doc1 = doc(db, "certificates", certId);
    batch.delete(doc1);

    const doc2 = doc(db, "transactions", transId);
    batch.delete(doc2);

    const doc3 = doc(db, "exams_completed", examId);
    batch.set(
      doc3,
      {
        payment: "Unpaid",
        refunded: true,
        status: "Complete",
      },
      {
        merge: true,
      }
    );
    await batch.commit();
  } catch (error) {}
};
export const updateAdminExams = async (id: string, data: any) => {
  try {
    const batch = writeBatch(db);
    const completedExamDocRef = doc(db, "exams_completed", id);
    const resp = await queryData("certificates", "examId", id);
    batch.set(
      completedExamDocRef,
      { payment: data.payment, score: data.score },
      { merge: true }
    );
    if (resp.status && resp.fullData.length > 0) {
      const certificateDocRef = doc(db, "certificates", resp.data.docId);
      batch.set(
        certificateDocRef,
        { paid: data.payment === "Paid" ? true : false },
        { merge: true }
      );
    }

    await batch.commit();
    return {
      status: true,
    };
  } catch (error) {
    return {
      status: false,
    };
  }
};
